// JOBE-0105: ProjectRow — single body row in the Projects Index table.
//
// Columns per spec §Decision 3 (left-to-right):
//   1. status dot          (payload.status enum → colour dot)
//   2. Project · summary   (name + lede + URN chip)
//   3. Status pill         (ADR-0023 enum verbatim: Active/Archived/Complete)
//   4. Owner               (relations.assignedTo[0])
//   5. Tasks               (counts.tasks from ?expand=rollups)
//   6. Documents           (counts.documents)
//   7. Entities            (sum of counts.entities_by_kind values
//                          excluding task + document)
//
// Whole-row click navigates to `/projects/{urn}` via useNavigate.
// Title click is the same target; redundant for a11y per spec.

import React from 'react';
import { useNavigate } from 'react-router-dom';

// urnSlug — small helper for the URN chip column. Mirrors lib/relTime's
// urnSlug shape (last colon-separated segment).
function urnSlug(urn) {
  if (!urn || typeof urn !== 'string') return '';
  const idx = urn.lastIndexOf(':');
  return idx >= 0 ? urn.slice(idx + 1) : urn;
}

// readProjectField — read a top-level field, falling back to payload.X.
// The BFF emits `payload` as a json.RawMessage so practical UIs read
// the parsed shape under `payload` OR a hoisted top-level field. The
// JOBE-0106 fixtures use the hoisted form, so we honour both.
function readProjectField(row, key) {
  if (!row) return undefined;
  if (row[key] !== undefined) return row[key];
  if (row.payload && row.payload[key] !== undefined) return row.payload[key];
  return undefined;
}

// readRelations — mirror of readProjectField for the relations bag.
function readRelations(row) {
  if (!row) return {};
  if (row.relations && typeof row.relations === 'object') return row.relations;
  if (row.payload && typeof row.payload === 'object' && row.payload.relations) {
    return row.payload.relations;
  }
  return {};
}

// Status enum per ADR-0023. Map to a colour bucket for the dot and a
// label for the pill (display verbatim, capitalised).
const STATUS_LABEL = {
  active: 'Active',
  archived: 'Archived',
  complete: 'Complete',
};
const STATUS_DOT_CLASS = {
  active: 'projects-row-status-dot-ok',
  archived: 'projects-row-status-dot-muted',
  complete: 'projects-row-status-dot-soft',
};

function entitiesTotal(counts) {
  if (!counts || typeof counts !== 'object') return null;
  const byKind = counts.entities_by_kind;
  if (!byKind || typeof byKind !== 'object') return null;
  let total = 0;
  for (const [kind, n] of Object.entries(byKind)) {
    if (kind === 'task' || kind === 'document') continue;
    if (typeof n === 'number' && Number.isFinite(n)) {
      total += n;
    }
  }
  return total;
}

// resolveOwnerDisplay — best-effort owner display. The Index does not
// round-trip per-owner /api/entities/{urn} resolutions in v1; we render
// the URN slug as a deterministic fallback so the column always renders
// non-empty text. A future spec wires a roster lookup.
function resolveOwnerDisplay(ownerURN, roster) {
  if (!ownerURN) return null;
  if (roster && Array.isArray(roster.humans)) {
    const found = roster.humans.find((p) => p?.urn === ownerURN);
    if (found) return found.displayName || found.name || urnSlug(ownerURN);
  }
  if (roster && Array.isArray(roster.agents)) {
    const found = roster.agents.find((p) => p?.urn === ownerURN);
    if (found) return found.displayName || found.name || urnSlug(ownerURN);
  }
  return urnSlug(ownerURN);
}

export function ProjectRow({ row, selected, roster, onSelect }) {
  const navigate = useNavigate();
  const urn = row?.urn || '';
  const name = readProjectField(row, 'name') || urn;
  const summary = readProjectField(row, 'summary') || readProjectField(row, 'lede') || '';
  const statusRaw = readProjectField(row, 'status') || '';
  const statusKey = typeof statusRaw === 'string' ? statusRaw.toLowerCase() : '';
  const statusLabel = STATUS_LABEL[statusKey] || (statusRaw ? String(statusRaw) : '—');
  const dotClass = STATUS_DOT_CLASS[statusKey] || 'projects-row-status-dot-unknown';
  const relations = readRelations(row);
  const ownerURN = Array.isArray(relations.assignedTo) ? relations.assignedTo[0] : null;
  const ownerLabel = resolveOwnerDisplay(ownerURN, roster);
  const counts = row?.counts || null;
  const tasks = counts && typeof counts.tasks === 'number' ? counts.tasks : null;
  const documents = counts && typeof counts.documents === 'number' ? counts.documents : null;
  const entities = counts ? entitiesTotal(counts) : null;

  const onRowClick = (e) => {
    // Don't navigate when the user clicks an explicit inner button.
    if (e.target && e.target.closest && e.target.closest('button')) return;
    if (onSelect) onSelect(urn);
    navigate(`/projects/${encodeURIComponent(urn)}`);
  };
  const onRowKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onSelect) onSelect(urn);
      navigate(`/projects/${encodeURIComponent(urn)}`);
    }
  };

  return (
    <div
      className={`projects-row${selected ? ' projects-row-selected' : ''}`}
      role="link"
      tabIndex={0}
      data-testid={`projects-row-${urn}`}
      data-project-urn={urn}
      onClick={onRowClick}
      onKeyDown={onRowKeyDown}
      aria-label={`Open ${name}`}
    >
      <div
        className={`projects-row-cell projects-row-status-dot ${dotClass}`}
        title={statusLabel}
        aria-label={`Status: ${statusLabel}`}
      />
      <div className="projects-row-cell projects-row-title">
        <div className="projects-row-name">{name}</div>
        {summary ? (
          <div className="projects-row-summary muted">{summary}</div>
        ) : null}
        <div className="projects-row-urn-chip">{urnSlug(urn)}</div>
      </div>
      <div className="projects-row-cell projects-row-pill">
        <span className={`projects-row-pill-tag projects-row-pill-${statusKey || 'unknown'}`}>
          {statusLabel}
        </span>
      </div>
      <div className="projects-row-cell projects-row-owner" data-testid="projects-row-owner">
        {ownerLabel || <span className="muted">—</span>}
      </div>
      <div className="projects-row-cell projects-row-count" data-testid="projects-row-tasks">
        {tasks == null ? <span className="muted">—</span> : tasks}
      </div>
      <div className="projects-row-cell projects-row-count" data-testid="projects-row-documents">
        {documents == null ? <span className="muted">—</span> : documents}
      </div>
      <div className="projects-row-cell projects-row-count" data-testid="projects-row-entities">
        {entities == null ? <span className="muted">—</span> : entities}
      </div>
    </div>
  );
}
